import { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router'
import { LoginStep, useAuthentication } from '@heidelbergmaterials/fcl-authclient'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faWrench } from '@fortawesome/free-solid-svg-icons'
import { ThemeToggle } from './ThemeToggle'
import { AssetQuickSearch } from './AssetQuickSearch'
import { ENV } from '../settings'
import IconButton from '@mui/material/IconButton'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Tooltip from '@mui/material/Tooltip'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'

const navLinks = [
  { to: '/', label: 'Home', match: (path: string) => path === '/' },
  { to: '/assets', label: 'Assets', match: (path: string) => path.startsWith('/assets') },
  { to: '/plants', label: 'Plants', match: (path: string) => path.startsWith('/plants') },
  { to: '/clock-readings', label: 'Clock Readings', match: (path: string) => path.startsWith('/clock-readings') },
  { to: '/asset-mapping', label: 'Asset Mapping', match: (path: string) => path.startsWith('/asset-mapping') },
];

export function Navigation() {
  const { startLogoutFlow, decodedAccessToken, loginStep } = useAuthentication();
  const isLoggedIn = loginStep === LoginStep.Authenticated || (import.meta.env.DEV && !ENV.auth_enabled);
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [assetsMenuAnchor, setAssetsMenuAnchor] = useState<HTMLElement | null>(null);

  const tokenRole = decodedAccessToken?.role;
  const tokenRoles = (decodedAccessToken as ({ roles?: string[]; role?: string } | null))?.roles ?? [];
  const normalizedRoles = [tokenRole, ...tokenRoles]
    .filter((value): value is string => Boolean(value))
    .map((value) => value.toLowerCase());
  const isFaimAdmin = normalizedRoles.some((value) =>
    value === 'admin'
    || value === 'globaladmin'
    || value === 'faimadmin'
    || value === 'faimglobaladmin'
  );

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 1);
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      <header className="fixed top-0 left-0 right-0 bg-navigation-bg z-50 border-b border-border shadow-sm transition-colors duration-200">
        <div className="max-w-[1440px] mx-auto px-6 relative">
          {/* Green brand block — inside container, overlaps below nav */}
          <Link
            to="/"
            className="absolute top-0 left-0 w-[130px] sm:w-[220px] bg-hc-green flex items-center justify-center shadow-md rounded-br-2xl z-[60] transition-all duration-400 ease-in-out"
            style={{ height: isScrolled ? '44px' : '100px', padding: '12px 16px' }}
          >
            <img src="/static/hclogo.png" alt="Heidelberg Materials" className="w-[100px] sm:w-[150px] object-contain" />
          </Link>

          {/* Top row: user info + sign out */}
          <div className="flex items-center justify-end gap-3 px-4 transition-all duration-400 ease-in-out" style={{ opacity: isScrolled ? 0 : 1, height: isScrolled ? 0 : '32px', overflow: 'hidden' }}>
            {isLoggedIn && isFaimAdmin && (
              <Link
                to="/administration"
                title="Administration"
                className="text-foreground-faded hover:text-primary leading-none"
              >
                <FontAwesomeIcon icon={faWrench} />
              </Link>
            )}
            {isLoggedIn && decodedAccessToken && (
              <span className="text-sm text-primary font-medium">{decodedAccessToken.name}</span>
            )}
            <ThemeToggle />
            <button
              onClick={() => void startLogoutFlow()}
              className="text-sm text-foreground-faded hover:text-primary"
            >
              Sign out
            </button>
          </div>
          {/* Bottom row: nav links on right */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:h-11">
            {isLoggedIn && (
              <div className="ml-[140px] sm:ml-[230px] min-w-0 lg:shrink-0 [&_.MuiAutocomplete-root]:w-full lg:[&_.MuiAutocomplete-root]:w-[360px]">
                <AssetQuickSearch />
              </div>
            )}
            <nav className="ml-[140px] sm:ml-[230px] lg:ml-auto min-w-0 flex items-center gap-2 px-1 sm:px-4 overflow-x-auto">
              {navLinks.map((link) => (
                <div
                  key={link.to}
                  className={`shrink-0 text-base h-11 flex items-center border-b-2 transition-colors ${link.match(location.pathname) ? 'border-primary text-foreground font-medium' : 'border-transparent text-foreground-faded hover:text-primary hover:border-primary'}`}
                >
                  <Link to={link.to} className="px-3 h-11 flex items-center">{link.label}</Link>
                  {link.to === '/assets' && (
                    <Tooltip title="Assets menu">
                      <IconButton size="small" aria-label="Assets menu"
                        aria-haspopup="menu" aria-expanded={Boolean(assetsMenuAnchor)}
                        onClick={(event) => setAssetsMenuAnchor(event.currentTarget)}>
                        <ExpandMoreIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </div>
              ))}
            </nav>
          </div>
        </div>
      </header>
      <Menu anchorEl={assetsMenuAnchor} open={Boolean(assetsMenuAnchor)} onClose={() => setAssetsMenuAnchor(null)}>
        <MenuItem component={Link} to="/assets" onClick={() => setAssetsMenuAnchor(null)}>Asset Search</MenuItem>
        <MenuItem component={Link} to="/assets/maintenance" onClick={() => setAssetsMenuAnchor(null)}>Maintenance Task Search</MenuItem>
      </Menu>
      {/* Spacer to push content below fixed nav */}
      <div className="h-[116px] lg:h-[100px]" />
    </>
  );
}
